import { Command } from 'commander';
import { spawnSync } from 'child_process';
import { join } from 'path';
import fs from 'fs-extra';
import chalk from 'chalk';
import { logger } from '../lib/logger.js';

// ── Container engine detection ─────────────────────────────────────────────

type EngineName = 'docker' | 'podman';

interface EngineStatus {
  name: EngineName;
  installed: boolean;
  running: boolean;
  cmd: string;
}

/**
 * Resolve the actual command/path for a container engine.
 * On Windows, Podman may be installed but not in PATH — check known locations.
 */
function resolveEngineCmd(name: EngineName): string {
  // First try the command directly (in PATH)
  try {
    const result = spawnSync(name, ['--version'], {
      encoding: 'utf-8', timeout: 5000, stdio: 'pipe',
    });
    if (result.status === 0) return name;
  } catch { /* not in PATH */ }

  // On Windows, check known install paths
  if (process.platform === 'win32' && name === 'podman') {
    const knownPaths = [
      'C:\\Program Files\\RedHat\\Podman\\podman.exe',
      join(process.env.LOCALAPPDATA || '', 'RedHat', 'Podman', 'podman.exe'),
    ];
    for (const p of knownPaths) {
      if (fs.existsSync(p)) {
        try {
          const result = spawnSync(p, ['--version'], {
            encoding: 'utf-8', timeout: 5000, stdio: 'pipe',
          });
          if (result.status === 0) return p;
        } catch { /* not working */ }
      }
    }
  }

  return name; // fallback to name (will fail at runtime)
}

function checkEngine(name: EngineName): EngineStatus {
  const cmd = resolveEngineCmd(name);
  try {
    const version = spawnSync(cmd, ['--version'], {
      encoding: 'utf-8', timeout: 5000, stdio: 'pipe',
    });
    if (version.status !== 0) {
      return { name, installed: false, running: false, cmd };
    }

    // Both Docker and Podman need a backend running (daemon or machine)
    const info = spawnSync(cmd, ['info'], {
      encoding: 'utf-8', timeout: 10000, stdio: 'pipe',
    });
    return { name, installed: true, running: info.status === 0, cmd };
  } catch {
    return { name, installed: false, running: false, cmd };
  }
}

/**
 * Check Windows virtualization prerequisites: WSL installed + Hyper-V/VMP enabled.
 * Returns null if OK, or the issue type.
 */
function checkWindowsVirtualization(): 'wsl-missing' | 'hyperv-disabled' | null {
  // 1. WSL installed?
  try {
    const r = spawnSync('wsl', ['--status'], { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' });
    if (r.status !== 0) return 'wsl-missing';
  } catch { return 'wsl-missing'; }

  // 2. Check wsl --status output for virtualization hints
  try {
    const r = spawnSync('wsl', ['--list', '--quiet'], { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' });
    const output = ((r.stdout || '') + (r.stderr || '')).toLowerCase();
    if (/virtual machine platform|hypervisor/i.test(output) && r.status !== 0) {
      return 'hyperv-disabled';
    }
  } catch { /* ignore */ }

  // 3. PowerShell check: VirtualMachinePlatform feature
  try {
    const r = spawnSync('powershell', [
      '-NoProfile', '-Command',
      '(Get-WindowsOptionalFeature -Online -FeatureName VirtualMachinePlatform).State',
    ], { encoding: 'utf-8', timeout: 10000, stdio: 'pipe' });
    const state = (r.stdout || '').trim();
    if (state === 'Disabled') return 'hyperv-disabled';
  } catch { /* PowerShell not available or not admin — skip */ }

  return null;
}

function detectEngine(): EngineStatus | null {
  // Try Docker first, then Podman
  const docker = checkEngine('docker');
  if (docker.installed && docker.running) return docker;

  const podman = checkEngine('podman');
  if (podman.installed && podman.running) {
    logger.info(`Using ${chalk.cyan('Podman')} (Docker not found or not running)`);
    return podman;
  }

  // Podman installed but machine not running
  if (podman.installed && !podman.running) {
    const virtIssue = process.platform === 'win32' ? checkWindowsVirtualization() : null;

    if (virtIssue === 'wsl-missing') {
      logger.error('WSL2 is required by Podman but is not installed.');
      console.log();
      console.log(`  Install WSL2 first (run as Administrator):`);
      console.log(`    ${chalk.cyan('wsl --install')}`);
      console.log(`  Restart your PC, then:`);
      console.log(`    ${chalk.cyan('podman machine init')}`);
      console.log(`    ${chalk.cyan('podman machine start')}`);
    } else if (virtIssue === 'hyperv-disabled') {
      logger.error('Hardware virtualization is not enabled.');
      console.log();
      console.log(`  To build Docker images, you need to enable virtualization:`);
      console.log(`    1. Enable VT-x (Intel) or SVM (AMD) in your BIOS/UEFI settings`);
      console.log(`    2. Then run as Administrator:`);
      console.log(`       ${chalk.cyan('wsl --install --no-distribution')}`);
      console.log(`    3. Restart your PC, then:`);
      console.log(`       ${chalk.cyan('podman machine init')}`);
      console.log(`       ${chalk.cyan('podman machine start')}`);
    } else {
      logger.error('Podman is installed but the machine is not running.');
      console.log();
      console.log(`  Initialize and start the Podman machine:`);
      console.log(`    ${chalk.cyan('podman machine init')}    (first time only)`);
      console.log(`    ${chalk.cyan('podman machine start')}`);
    }
    if (docker.installed) {
      console.log();
      console.log(`  Or start Docker Desktop instead.`);
    }
    console.log();
    return null;
  }

  // Docker installed but daemon not running, no Podman
  if (docker.installed && !docker.running) {
    logger.error('Docker is installed but the daemon is not running.');
    console.log();
    console.log(`  Start Docker Desktop and wait for it to be ready.`);
    console.log(`  Or install Podman: ${chalk.cyan('https://podman.io/docs/installation')}`);
    console.log();
    return null;
  }

  // Nothing installed
  logger.error('No container engine found (Docker or Podman).');
  console.log();
  console.log(`  Install one of the following:`);
  console.log(`    Docker Desktop: ${chalk.cyan('https://docs.docker.com/get-docker/')}`);
  console.log(`    Podman:         ${chalk.cyan('https://podman.io/docs/installation')}`);
  console.log();
  return null;
}

function requireEngine(): EngineStatus | null {
  return detectEngine();
}

// ── Project detection ──────────────────────────────────────────────────────

function findComposeFile(): string | null {
  const cwd = process.cwd();
  const composePath = join(cwd, 'docker-images', 'docker-compose.yml');
  if (fs.existsSync(composePath)) {
    return composePath;
  }
  return null;
}

function requireComposeFile(): string | null {
  const composePath = findComposeFile();
  if (!composePath) {
    logger.error('No docker-compose.yml found in docker-images/');
    console.log();
    console.log(`  Make sure you are in a SmartStack project root directory.`);
    console.log(`  Run ${chalk.cyan('ss upgrade')} to generate the Docker files.`);
    console.log();
    return null;
  }
  return composePath;
}

// ── Compose runner ─────────────────────────────────────────────────────────

/**
 * When Podman runs alongside Docker Desktop (installed but stopped),
 * Podman inherits Docker's credential store (docker-credential-desktop.exe)
 * which fails for public images. Bypass by using a clean auth file.
 */
function getPodmanEnv(engine: EngineStatus): Record<string, string> {
  if (engine.name !== 'podman') return {};

  // Check if Docker's credential store is causing issues
  const dockerConfigPath = join(process.env.USERPROFILE || process.env.HOME || '', '.docker', 'config.json');
  try {
    if (fs.existsSync(dockerConfigPath)) {
      const config = JSON.parse(fs.readFileSync(dockerConfigPath, 'utf-8'));
      if (config.credsStore === 'desktop' || config.credsStore === 'wincred') {
        // Docker Desktop credential store is configured but Docker isn't running
        // Use a clean auth file to bypass
        const cleanAuthPath = join(process.env.TEMP || '/tmp', 'podman-auth.json');
        if (!fs.existsSync(cleanAuthPath)) {
          fs.writeFileSync(cleanAuthPath, '{}', 'utf-8');
        }
        return { REGISTRY_AUTH_FILE: cleanAuthPath };
      }
    }
  } catch { /* ignore */ }
  return {};
}

function runCompose(engine: EngineStatus, composePath: string, args: string[]): number {
  const extraEnv = getPodmanEnv(engine);
  if (Object.keys(extraEnv).length > 0) {
    logger.info('Bypassing Docker Desktop credential store for Podman');
  }

  const result = spawnSync(engine.cmd, ['compose', '-f', composePath, ...args], {
    encoding: 'utf-8',
    stdio: 'pipe',
    cwd: process.cwd(),
    env: { ...process.env, ...extraEnv },
  });

  // Stream output
  if (result.stdout) process.stdout.write(result.stdout);
  if (result.stderr) process.stderr.write(result.stderr);

  // Diagnose common errors
  if (result.status !== 0) {
    const output = (result.stdout || '') + (result.stderr || '');
    diagnoseError(engine, output);
  }

  return result.status ?? 1;
}

function diagnoseError(engine: EngineStatus, output: string): void {
  // Disk space
  if (/no space left|disk full/i.test(output)) {
    console.log();
    logger.warning('Disk space error detected.');
    console.log(`  Free up space with: ${chalk.cyan(`${engine.name} system prune -a`)}`);
    console.log();
  }

  // Network / DNS
  if (/could not resolve|network.*unreachable|timeout.*connect/i.test(output)) {
    console.log();
    logger.warning('Network error detected. Check your internet connection.');
    console.log();
  }

  // Podman machine not running (shouldn't happen at this point but just in case)
  if (/unable to connect to Podman socket|podman machine init/i.test(output)) {
    console.log();
    logger.warning('Podman machine is not running.');
    console.log(`    ${chalk.cyan('podman machine init')}    (first time only)`);
    console.log(`    ${chalk.cyan('podman machine start')}`);
    console.log();
  }
}

// ── Subcommands ────────────────────────────────────────────────────────────

export const dockerCommand = new Command('docker')
  .description('Build and run SmartStack Docker images (Docker or Podman)')
  .addCommand(
    new Command('up')
      .description('Build images and start containers')
      .option('-d, --detach', 'Run containers in the background')
      .option('--no-build', 'Skip image build, use existing images')
      .action(async (options) => {
        const engine = requireEngine();
        if (!engine) return;
        const composePath = requireComposeFile();
        if (!composePath) return;

        logger.header('SmartStack Docker Up');

        const args = ['up'];
        if (options.build !== false) args.push('--build');
        if (options.detach) args.push('-d');

        const code = runCompose(engine, composePath, args);
        if (code !== 0) {
          logger.error(`${engine.name} compose up exited with code ${code}`);
          process.exitCode = code;
        }
      }),
  )
  .addCommand(
    new Command('down')
      .description('Stop and remove containers')
      .option('-v, --volumes', 'Also remove volumes')
      .action(async (options) => {
        const engine = requireEngine();
        if (!engine) return;
        const composePath = requireComposeFile();
        if (!composePath) return;

        logger.header('SmartStack Docker Down');

        const args = ['down'];
        if (options.volumes) args.push('-v');

        const code = runCompose(engine, composePath, args);
        if (code !== 0) {
          logger.error(`${engine.name} compose down exited with code ${code}`);
          process.exitCode = code;
        }
      }),
  )
  .addCommand(
    new Command('build')
      .description('Build images without starting containers')
      .option('--no-cache', 'Build without using cache')
      .action(async (options) => {
        const engine = requireEngine();
        if (!engine) return;
        const composePath = requireComposeFile();
        if (!composePath) return;

        logger.header('SmartStack Docker Build');

        const args = ['build'];
        if (options.cache === false) args.push('--no-cache');

        const code = runCompose(engine, composePath, args);
        if (code !== 0) {
          logger.error(`${engine.name} compose build exited with code ${code}`);
          process.exitCode = code;
        }
      }),
  )
  .addCommand(
    new Command('status')
      .description('Show container status')
      .action(async () => {
        const engine = requireEngine();
        if (!engine) return;
        const composePath = requireComposeFile();
        if (!composePath) return;

        const code = runCompose(engine, composePath, ['ps', '-a']);
        if (code !== 0) {
          process.exitCode = code;
        }
      }),
  )
  .addCommand(
    new Command('logs')
      .description('Show container logs')
      .option('-f, --follow', 'Follow log output')
      .option('--tail <lines>', 'Number of lines to show from the end', '100')
      .argument('[service]', 'Service name (backend or frontend)')
      .action(async (service, options) => {
        const engine = requireEngine();
        if (!engine) return;
        const composePath = requireComposeFile();
        if (!composePath) return;

        const args = ['logs', '--tail', options.tail];
        if (options.follow) args.push('-f');
        if (service) args.push(service);

        const code = runCompose(engine, composePath, args);
        if (code !== 0) {
          process.exitCode = code;
        }
      }),
  )
  .action(() => {
    // Default: show help when `ss docker` is called without subcommand
    dockerCommand.outputHelp();
  });
